/**
 * Spec 218 PR5 — Generate + download a Verifactu invoice PDF on the web.
 *
 * Mobile uses `expo-print` (native HTML→PDF on the device, with
 * selectable text). Web has no such API; we lean on the deps already
 * in the bundle:
 *   - `jspdf` to assemble the document and trigger the blob,
 *   - `html2canvas` (used by jsPDF.html() under the hood) to rasterise
 *     the template before stitching it into pages.
 *
 * KNOWN TRADE-OFF: jsPDF.html() rasterises the HTML to images. The
 * resulting PDF is NOT byte-equivalent to mobile's expo-print output:
 * mobile produces selectable text; web produces an image-based PDF.
 * For a Verifactu invoice this is acceptable (the legal QR + the
 * series/number are still rendered, the AEAT-side hash lives at the
 * AEAT, not in the PDF), but accessibility tooling will not see the
 * text. If a future revision needs selectable text we'd switch to the
 * native jsPDF API (doc.text + doc.rect) at the cost of a much heavier
 * port of the template.
 *
 * The HTML template lives in `invoicePdfTemplate.ts`; it mirrors
 * `mobile/src/utils/invoicePdfTemplate.ts` in structure (NOT byte-for-
 * byte: web removes the unused `t` arg and accepts a `cancelled` flag
 * that mobile doesn't currently surface).
 *
 * Why jsPDF.html() and not window.print(): html() returns a Blob we can
 * save deterministically with a stable filename. window.print() opens
 * the OS dialog and depends on user choices; it also can't be exercised
 * by Playwright's download channel.
 */
import { buildInvoicePdfHtml } from './invoicePdfTemplate';
import type { EmitResponse } from '../sync/invoicing';
import type { FiscalProfile, InvoiceDraft } from '@cuadrilla/types/invoice';

export interface InvoicePdfArgs {
  profile: FiscalProfile;
  draft: InvoiceDraft;
  response: EmitResponse;
  /** Mark the PDF as cancelled (watermark + banner). Passed through to
   *  `buildInvoicePdfHtml`. The detail screen sets this from the
   *  Invoice's `cancelled` flag so an anulled invoice can never be
   *  re-shared as if still in force. */
  cancelled?: boolean;
}

const SAFE_FILENAME_RE = /[^a-zA-Z0-9._-]/g;

/** Slug-friendly transform of `series`/`number` so a value like
 *  `F/2026` doesn't blow up the OS download path or produce a path
 *  traversal. Replaces every char outside `[A-Za-z0-9._-]` with `_`. */
function safeFilenamePart(value: string): string {
  return value.replace(SAFE_FILENAME_RE, '_');
}

/**
 * Build the Blob without saving to disk. Useful for Playwright tests
 * that want to inspect the bytes, or for a future "Send by email"
 * feature that attaches the PDF.
 */
export async function buildInvoicePdfBlob(args: InvoicePdfArgs): Promise<Blob> {
  const { profile, draft, response, cancelled } = args;
  // Dynamic import keeps jspdf out of the route-list initial bundle.
  // It only loads when the user actually clicks "Descargar PDF".
  const { jsPDF } = await import('jspdf');
  const html = buildInvoicePdfHtml({ profile, draft, response, locale: 'es', cancelled });

  // Render the HTML inside a detached, off-screen container so jsPDF.html()
  // can measure layout (the call internally uses html2canvas, which needs
  // a real DOM node — strings alone are not enough).
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.top = '-10000px';
  host.style.left = '0';
  host.style.width = '794px'; // A4 width in CSS px @ 96dpi (210mm ≈ 794px)
  host.innerHTML = html;
  document.body.appendChild(host);
  // Use the body extracted from the template so the @page CSS at the
  // top of the file doesn't bleed into the host document.
  const inner = host.querySelector('body') ?? host;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  try {
    await doc.html(inner as HTMLElement, {
      // A4 = 210x297 mm; leave 14 mm margins to match the template's @page rule.
      x: 14,
      y: 14,
      width: 182,
      windowWidth: 794,
      autoPaging: 'text',
    });
  } finally {
    host.remove();
  }
  return doc.output('blob');
}

/**
 * Build + trigger a browser download. Filename is `<series>-<number>.pdf`
 * to match the mobile share-sheet behaviour and to keep filenames stable
 * across devices for the same invoice.
 */
export async function downloadInvoicePdf(args: InvoicePdfArgs): Promise<void> {
  const blob = await buildInvoicePdfBlob(args);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Sanitise series/number — Spanish series like "F/2026" contain `/`
  // which the browser would interpret as a path separator on the
  // suggested name. Replace anything outside `[A-Za-z0-9._-]` with `_`.
  a.download = `${safeFilenamePart(args.draft.series)}-${safeFilenamePart(args.draft.number)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer the revoke so the browser has time to start the download from
  // the blob URL before we drop it. Anchor click is sync, but Chromium
  // pulls bytes lazily; revoking immediately can race the request.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
